import * as THREE from 'three';
import { glass, material, plain } from './textures.js';
import { makeFurniture } from './furniture.js';

/**
 * 設計データ（src/data/plans/*.js）から家の3Dモデルを組み立てる。
 *
 * 設計データの形:
 * - rooms:     床と天井。{ x:[x0,x1], z:[z0,z1], y, h, floor, ceil, thick, ceilThick }
 * - walls:     壁。{ from:[x,z], to:[x,z], y, h, t, mat, openings:[{ a, w, sill, top, fill, frame }] }
 *              a = from からの距離。fill は 'door'（何も入れない）'window' 'shoji' 'glass' 'lattice'
 * - stairs:    直階段。{ x0, x1, z0, z1, y0, y1, steps } x0 側が下、x1 側が上
 * - furniture: 家具。{ type, x, y, z, rot, ...家具ごとの寸法 }
 * - lights:    照明。{ x, y, z, color, intensity, distance }
 *
 * 座標は m。x は東西、z は南北（設計図の下方向が +z）、y は高さ。
 */
export function buildHouse(plan) {
  const group = new THREE.Group();
  group.name = `house:${plan.id}`;

  /*
   * 質感の貼り方。
   * - 通常: 面の大きさに合わせて質感の繰り返し数を決める（面ごとに材質を作る）
   * - worldUv: 部屋の座標（m）をそのまま質感の座標にする。材質は質感ごとに1つで済むので、
   *   スキャンから起こした何百もの小さな面でもメモリを食わない。面の継ぎ目で模様がつながる利点もある
   */
  const worldUv = plan.worldUv === true;
  const meshes = [];
  const options = {
    material: (name, w, h) => (worldUv ? material(name, 1, 1) : material(name, w, h)),
    register: (mesh) => meshes.push(mesh),
  };
  for (const room of plan.rooms) addRoom(group, room, options);
  for (const wall of plan.walls) addWall(group, wall, options);
  for (const stair of plan.stairs ?? []) addStairs(group, stair, options);
  if (worldUv) {
    group.updateMatrixWorld(true);
    for (const mesh of meshes) applyWorldUv(mesh);
  }
  // 家具は「これは何番目の何か」が分かるようにしておく。
  // 画面でクリックして消す機能（furnitureRemover.js）がこの印を見て家具の単位を判断する。
  (plan.furniture ?? []).forEach((item, index) => {
    const furniture = makeFurniture(item);
    furniture.userData.furniture = {
      index,
      type: item.type,
      label: FURNITURE_LABELS[item.type] ?? item.type,
      // 見積りの品目と結びつける名前。付いていれば値段を出せる（src/ui/priceTags.js）
      sku: item.sku ?? null,
    };
    group.add(furniture);
  });

  // 全体をやわらかく照らす（Lambert 材質用）。部屋ごとの暖かい光は lights で足す
  const sky = new THREE.HemisphereLight(0xfff4e4, 0x7a6a58, plan.ambient ?? 1.5);
  group.add(sky);
  const sun = new THREE.DirectionalLight(0xfff0dc, plan.sun ?? 0.9);
  sun.position.set(4, 12, 6);
  group.add(sun);

  /*
   * 影。上から見たときに家具や壁が床に影を落とすと、立体に見えて部屋の形が分かりやすくなる。
   * 影の範囲は建物を囲む大きさに合わせる（既定のままだと建物の一部しか影が出ない）。
   * スキャンから起こした CG（worldUv）は面が数百枚あり影の計算が重いので、そちらでは使わない。
   */
  if (plan.shadows === true) {
    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const reach = Math.max(size.x, size.z) * 0.75 + 2;
    sun.castShadow = true;
    sun.shadow.mapSize.set(SHADOW_MAP, SHADOW_MAP);
    sun.shadow.camera.left = -reach;
    sun.shadow.camera.right = reach;
    sun.shadow.camera.top = reach;
    sun.shadow.camera.bottom = -reach;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = size.y + reach * 3;
    sun.shadow.bias = -0.0015;
    sun.shadow.normalBias = 0.02;
    // 光の向きは建物の中心へ。斜め上から差す形にして、床に家具の影が落ちるようにする
    sun.position.set(center.x + reach * 0.5, box.max.y + reach, center.z + reach * 0.6);
    sun.target.position.copy(center);
    group.add(sun.target);
    group.traverse((node) => {
      if (!node.isMesh) return;
      node.castShadow = true;
      node.receiveShadow = true;
    });
  }
  for (const light of plan.lights ?? []) {
    const point = new THREE.PointLight(light.color ?? 0xffd7a8, light.intensity ?? 6, light.distance ?? 6, 1.6);
    point.position.set(light.x, light.y, light.z);
    group.add(point);
  }

  return group;
}

/** 床（上面は floor の質感）と天井（下面は ceil の質感） */
function addRoom(group, room, options) {
  const [x0, x1] = room.x;
  const [z0, z1] = room.z;
  const w = x1 - x0;
  const d = z1 - z0;
  const y = room.y ?? 0;
  const thick = room.thick ?? 0.06;
  const side = plain(0x6e5a48);

  if (room.floor) {
    const floor = new THREE.Mesh(new THREE.BoxGeometry(w, thick, d), [
      side, side, options.material(room.floor, w, d), room.under ? options.material(room.under, w, d) : side, side, side,
    ]);
    floor.position.set((x0 + x1) / 2, y - thick / 2, (z0 + z1) / 2);
    group.add(floor);
    options.register(floor);
  }

  if (room.ceil && room.h) {
    const ct = room.ceilThick ?? 0.1;
    const top = room.roof ? options.material(room.roof, w, d) : side;
    const ceiling = new THREE.Mesh(new THREE.BoxGeometry(w, ct, d), [
      side, side, top, options.material(room.ceil, w, d), side, side,
    ]);
    ceiling.position.set((x0 + x1) / 2, y + room.h + ct / 2, (z0 + z1) / 2);
    group.add(ceiling);
    options.register(ceiling);
  }
}

/**
 * 箱の各面の質感座標を、部屋の座標（m）に置き換える。
 * 上下の面は (x, z)、東西の面は (z, y)、南北の面は (x, y) をそのまま使う。
 * 質感は 1m 四方で描いてあるので、これで大きさに関係なく正しい縮尺で繰り返す。
 */
function applyWorldUv(mesh) {
  const { position, normal, uv } = mesh.geometry.attributes;
  const v = new THREE.Vector3();
  const n = new THREE.Vector3();
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
  for (let i = 0; i < position.count; i += 1) {
    v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
    n.fromBufferAttribute(normal, i).applyMatrix3(normalMatrix);
    if (Math.abs(n.y) > 0.5) uv.setXY(i, v.x, v.z);
    else if (Math.abs(n.x) > 0.5) uv.setXY(i, v.z, v.y);
    else uv.setXY(i, v.x, v.y);
  }
  uv.needsUpdate = true;
}

/**
 * 壁。開口部（ドア・窓）の所は、下（腰壁）と上（垂れ壁）だけ残す。
 * 開口部には枠と、指定があれば障子・ガラス・格子を入れる。
 */
function addWall(group, wall, options) {
  const [fx, fz] = wall.from;
  const [tx, tz] = wall.to;
  const length = Math.hypot(tx - fx, tz - fz);
  const angle = Math.atan2(-(tz - fz), tx - fx);
  const y = wall.y ?? 0;
  const h = wall.h ?? 2.4;
  const t = wall.t ?? 0.12;
  const mat = options.material(wall.mat ?? 'plaster', length, h);

  const holder = new THREE.Group();
  holder.position.set(fx, y, fz);
  holder.rotation.y = angle;
  group.add(holder);

  const piece = (a0, a1, y0, y1, m = mat) => {
    if (a1 - a0 <= 0.001 || y1 - y0 <= 0.001) return;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(a1 - a0, y1 - y0, t), m);
    mesh.position.set((a0 + a1) / 2, (y0 + y1) / 2, 0);
    holder.add(mesh);
    options.register(mesh);
  };

  const openings = [...(wall.openings ?? [])].sort((p, q) => p.a - q.a);
  let cursor = 0;
  for (const o of openings) {
    const sill = o.sill ?? 0;
    const top = o.top ?? 2.05;
    piece(cursor, o.a, 0, h);
    piece(o.a, o.a + o.w, 0, sill);
    piece(o.a, o.a + o.w, top, h);
    addOpeningFrame(holder, o.a, o.w, sill, top, t, o);
    cursor = o.a + o.w;
  }
  piece(cursor, length, 0, h);

  // 幅木（床との境目）
  if (wall.skirting !== false && y === 0) {
    const skirt = new THREE.Mesh(new THREE.BoxGeometry(length, 0.08, t + 0.02), plain(0x5a3d26));
    skirt.position.set(length / 2, 0.04, 0);
    holder.add(skirt);
  }
}

/** 開口部の木枠と、中に入れる建具 */
function addOpeningFrame(holder, a, w, sill, top, t, o) {
  const wood = plain(o.frameColor ?? 0x6b4a2b);
  const f = 0.05;
  const d = t + 0.03;
  const add = (x, yc, sx, sy) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, d), wood);
    m.position.set(x, yc, 0);
    holder.add(m);
  };
  if (o.frame !== false) {
    add(a + f / 2, (sill + top) / 2, f, top - sill);
    add(a + w - f / 2, (sill + top) / 2, f, top - sill);
    add(a + w / 2, top - f / 2, w, f);
    if (sill > 0) add(a + w / 2, sill + f / 2, w, f);
  }

  const fill = o.fill ?? 'door';
  if (fill === 'door') return;
  const inner = w - f * 2;
  const innerH = top - sill - f * (sill > 0 ? 2 : 1);
  const yc = sill + (sill > 0 ? f : 0) + innerH / 2;
  let m;
  if (fill === 'shoji') m = material('shoji', inner / 0.9, innerH / 0.9);
  else if (fill === 'lattice') m = material('lattice', inner / 0.8, innerH / 0.8);
  else if (fill === 'frosted') m = plain(0xe9f0f2, { transparent: true, opacity: 0.85 });
  else m = glass();
  const pane = new THREE.Mesh(new THREE.BoxGeometry(inner, innerH, 0.012), m);
  pane.position.set(a + w / 2, yc, 0);
  holder.add(pane);
  if (fill === 'glass' || fill === 'frosted') {
    // 窓の桟
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.03, innerH, d - 0.01), wood);
    bar.position.set(a + w / 2, yc, 0);
    holder.add(bar);
  }
}

/** 直階段。x0 側から x1 側へ上る。踏板ごとに下まで詰まった箱にする */
function addStairs(group, s, options) {
  const steps = s.steps ?? 13;
  const rise = (s.y1 - s.y0) / steps;
  const run = (s.x1 - s.x0) / steps;
  const width = s.z1 - s.z0;
  const tread = options.material('walnut', Math.abs(run), width);
  const riser = options.material('walnut', width, rise);
  for (let i = 1; i <= steps; i += 1) {
    const top = s.y0 + rise * i;
    const xa = s.x0 + run * (i - 1);
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(Math.abs(run), top - s.y0, width), [
      riser, riser, tread, riser, riser, riser,
    ]);
    mesh.position.set(xa + run / 2, s.y0 + (top - s.y0) / 2, (s.z0 + s.z1) / 2);
    group.add(mesh);
    options.register(mesh);
  }
  // 手すり
  const rail = new THREE.Mesh(new THREE.BoxGeometry(Math.abs(s.x1 - s.x0), 0.04, 0.04), plain(0x5a3d26));
  const cx = (s.x0 + s.x1) / 2;
  const railZ = s.railZ ?? s.z1;
  rail.position.set(cx, s.y0 + (s.y1 - s.y0) / 2 + 0.9, railZ);
  rail.rotation.z = -Math.atan2(s.y1 - s.y0, s.x1 - s.x0);
  group.add(rail);
}

/** 画面に出す家具の名前（クリックで消すときの表示に使う） */
/** 影のきめ細かさ。上げると影がなめらかになるが、作るのに時間とメモリを食う */
const SHADOW_MAP = 2048;

const FURNITURE_LABELS = Object.freeze({
  box: '収納・パネル',
  platform: '小上がり',
  kotatsu: '掘りごたつ',
  zabuton: '座布団',
  table: 'テーブル',
  chair: '椅子',
  sofa: 'ソファ',
  stool: 'スツール',
  bed: 'ベッド',
  bathtub: '浴槽',
  toilet: 'トイレ',
  basin: '洗面台',
  washer: '洗濯機',
  mirror: '鏡',
  shelf: '棚',
  chaise: '寝椅子',
  plant: '観葉植物',
  pendant: 'ペンダント照明',
  lantern: '提灯',
  spotRail: 'スポット照明',
  post: '柱',
  beam: '梁',
  kitchen: 'キッチン',
  counter: 'カウンター',
  heater: 'サウナストーブ',
  bench: 'ベンチ',
  picture: '額',
  ac: 'エアコン',
  glassDoor: 'ガラス戸',
  fusuma: '襖',
  slatPanel: '格子パネル',
  lamp: 'ランプ',
  sideTable: 'サイドテーブル',
  ottoman: 'オットマン',
  blind: 'ロールスクリーン',
  coveLight: '間接照明',
  fridge: '冷蔵庫',
  tv: 'テレビ',
  tvStand: 'テレビスタンド',
  dresser: 'ドレッサー',
  steelRack: 'スチールラック',
  screen: 'プロジェクタースクリーン',
  projector: 'プロジェクター',
  appliance: '家電',
  cluster: '家具',
});
